import { useCallback, useEffect, useState } from "react";
import { Btn, I } from "../../ui";
import { listProspects, listSisterBrowserQueue, previewSisterBrowserNecrologi } from "../../api";
import { useDemoScope } from "../../useDemoScope";
import type { ExtensionWorkerStatus } from "../../../api/extension-worker-status";
import { workerStall } from "../extension/worker-stall";

type GoFn = (name: string, args?: Record<string, unknown>) => void;

interface WorkCounts {
  daCercare: number | null;
  daCercareOltre: boolean;
  inCorso: number | null;
  daControllare: number | null;
  daInviare: number | null;
  conImmobili: number | null;
  worker: ExtensionWorkerStatus | null;
}

const PREVIEW_LIMIT = 200;
const ACTIVE_STATES = new Set(["queued", "claimed", "running"]);

function valueOf<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === "fulfilled" ? result.value : null;
}

async function loadCounts(): Promise<WorkCounts> {
  const [preview, queue, prospects] = await Promise.allSettled([
    previewSisterBrowserNecrologi({ limit: PREVIEW_LIMIT }),
    listSisterBrowserQueue(1),
    listProspects({ limit: 1 }),
  ]);
  const p = valueOf(preview);
  const q = valueOf(queue);
  const s = valueOf(prospects)?.stats;
  return {
    daCercare: p ? p.items.filter((i) => i.selectable && !i.duplicatePerson).length : null,
    daCercareOltre: (p?.items.length ?? 0) >= PREVIEW_LIMIT,
    inCorso: q ? q.byState.filter((b) => ACTIVE_STATES.has(b.state)).reduce((n, b) => n + b.count, 0) : null,
    daControllare: s ? s.omonimiFound : null,
    daInviare: s ? s.herobotReady : null,
    conImmobili: s ? s.visuraOk : null,
    worker: q?.worker ?? null,
  };
}

function format(n: number | null): string {
  return n === null ? "—" : n.toLocaleString("it-IT");
}

/** I quattro passi del lavoro, dal necrologio all'invio a Herobot. Ogni passo
 * porta nel punto dell'app dove si fa quell'azione. */
export function WorkToday({ onGo, onlineCount }: { onGo: GoFn; onlineCount: number }) {
  const [counts, setCounts] = useState<WorkCounts | null>(null);
  // Nella demo Herobot è spento: l'ultimo passo è guardare gli immobili trovati.
  const demo = useDemoScope() !== null;

  const load = useCallback(async () => {
    setCounts(await loadCounts());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const c = counts;
  // Servers without worker status fall back to the heartbeat: an open Chrome
  // is not enough, the queue loop must have been started from the popup.
  const stall = c?.worker ? workerStall(c.worker, c.inCorso ?? 0) : null;
  const stalled = c?.worker ? stall !== null : (c?.inCorso ?? 0) > 0 && onlineCount === 0;
  const notStarted = stall?.kind === "not_started";
  // Il pulsante principale va al passo che ha più bisogno dell'operatore.
  const lastCount = demo ? c?.conImmobili ?? null : c?.daInviare ?? null;
  const focus = (c?.daControllare ?? 0) > 0 ? 3 : !demo && (lastCount ?? 0) > 0 ? 4 : (c?.daCercare ?? 0) > 0 ? 1 : 0;

  return (
    <section className="work-today" aria-labelledby="work-today-title">
      <header className="work-today-head">
        <div>
          <h2 id="work-today-title">Il lavoro di oggi</h2>
          <p>{demo ? "Dal necrologio agli immobili trovati, in quattro passi." : "Dal necrologio all'invio a Herobot, in quattro passi."}</p>
        </div>
        <Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => void load()}>Aggiorna</Btn>
      </header>
      <ol className="work-steps">
        <li className={`work-step${focus === 1 ? " is-focus" : ""}`}>
          <span className="work-step-n" aria-hidden="true">1</span>
          <h3>Scegli chi cercare</h3>
          <p className="work-step-count">{format(c?.daCercare ?? null)}</p>
          <p className="work-step-text">
            {c?.daCercareOltre ? "necrologi recenti da cercare su SISTER (e altri ancora)" : "necrologi recenti non ancora cercati su SISTER"}
          </p>
          <Btn size="sm" variant={focus === 1 ? "primary" : ""} onClick={() => onGo("necrologi", { cerca: "1" })}>
            Scegli
          </Btn>
        </li>
        <li className={`work-step${stalled ? " is-warn" : ""}`}>
          <span className="work-step-n" aria-hidden="true">2</span>
          <h3>L'estensione cerca</h3>
          <p className="work-step-count">{format(c?.inCorso ?? null)}</p>
          <p className="work-step-text">
            {stalled
              ? notStarted
                ? "ricerche ferme: l'estensione è accesa ma la ricerca su SISTER non è avviata"
                : "ricerche ferme: nessuna estensione accesa"
              : onlineCount > 0
                ? `ricerche in corso · ${onlineCount} ${onlineCount === 1 ? "estensione accesa" : "estensioni accese"}`
                : "ricerche in corso"}
          </p>
          <Btn size="sm" variant={stalled ? "primary" : ""} onClick={() => onGo(notStarted ? "necrologi" : "extension")}>
            {notStarted ? "Come avviarla" : stalled ? "Accendi l'estensione" : "Estensione"}
          </Btn>
        </li>
        <li className={`work-step${focus === 3 ? " is-focus" : ""}`}>
          <span className="work-step-n" aria-hidden="true">3</span>
          <h3>Controlla gli omonimi</h3>
          <p className="work-step-count">{format(c?.daControllare ?? null)}</p>
          <p className="work-step-text">persone con più omonimi: scegli quella giusta</p>
          <Btn size="sm" variant={focus === 3 ? "primary" : ""} onClick={() => onGo("prospects", { vista: "da_controllare" })}>
            Controlla
          </Btn>
        </li>
        <li className={`work-step${focus === 4 ? " is-focus" : ""}`}>
          <span className="work-step-n" aria-hidden="true">4</span>
          <h3>{demo ? "Guarda gli immobili" : "Invia a Herobot"}</h3>
          <p className="work-step-count">{format(lastCount)}</p>
          <p className="work-step-text">{demo ? "persone con immobili trovati su SISTER" : "persone con immobili non ancora inviate"}</p>
          <Btn size="sm" variant={focus === 4 ? "primary" : ""} onClick={() => onGo("prospects", { vista: "con_immobili" })}>
            Apri
          </Btn>
        </li>
      </ol>
    </section>
  );
}
